/**
 * Auto-resize support for embedding the site in an iframe (e.g. a GoHighLevel
 * Custom HTML block). When the page runs inside a frame — or `?embed=1` is set —
 * it posts its content height to the parent so the host can size the iframe to
 * fit (no inner scrollbar, no fixed-height whitespace). The host listens for
 * `{ type: "bougie:height", height }` messages. No-op when not embedded.
 */

const MESSAGE_TYPE = "bougie:height";

// Backstop against a feedback runaway (host grows the iframe → any leftover
// `vh` content grows → we report a bigger height → …). The embed-mode CSS
// removes the viewport-relative heights that cause it, but this cap guarantees
// we can never post an absurd height even if a new `vh` slips in. The site is
// long but nowhere near this tall.
const MAX_HEIGHT = 20000;

function isEmbedded(): boolean {
  // Both checks are safe cross-origin: reading/comparing `window.parent` does
  // not throw, and `?embed=1` is an explicit opt-in for same-origin testing.
  if (new URLSearchParams(window.location.search).get("embed") === "1") return true;
  return window.parent != null && window.parent !== window;
}

function contentHeight(): number {
  const doc = document.documentElement;
  const body = document.body;
  return Math.min(
    MAX_HEIGHT,
    Math.ceil(
      Math.max(
        body?.scrollHeight ?? 0,
        body?.offsetHeight ?? 0,
        doc?.scrollHeight ?? 0,
        doc?.offsetHeight ?? 0,
      ),
    ),
  );
}

let started = false;

/** Begin reporting content height to the parent frame. Safe to call always —
 *  it returns immediately when the page isn't embedded, and is idempotent
 *  (guards against duplicate listeners/observers from HMR or a double call). */
export function initEmbedHeight(): void {
  if (typeof window === "undefined" || started || !isEmbedded()) return;
  started = true;

  // Mark the document as embedded so the embed-mode CSS (which drops
  // viewport-relative heights) applies before we take the first measurement.
  document.documentElement.classList.add("embed");

  let last = -1;
  const post = () => {
    const height = contentHeight();
    if (height === last || height <= 0) return;
    last = height;
    window.parent.postMessage({ type: MESSAGE_TYPE, height }, "*");
  };

  // Initial + staggered posts to catch web-font swaps and image loads that
  // change layout shortly after first paint.
  post();
  for (const delay of [150, 500, 1200]) window.setTimeout(post, delay);
  window.addEventListener("load", post);

  // Layout changes (responsive reflow, accordions/FAQ toggles).
  if (typeof ResizeObserver !== "undefined" && document.body) {
    new ResizeObserver(post).observe(document.body);
  }
  // In-iframe SPA navigation (trip/persona pages) changes content height.
  window.addEventListener("popstate", () => window.setTimeout(post, 100));
}
